import React, { useEffect, useState } from 'react';
import { Alert, Button, InputNumber, Select, Space, Typography } from 'antd';
import { nativeRequest } from './transport';

export type Pacing = {
  configuration: {
    empty_day_ms: number;
    news_item_ms: number;
    delivery: 'bulk' | 'staggered';
    news_interval_ms: number;
    news_quiet_ms: number;
  };
  revision: number;
  current_day: {
    date: string;
    active: Pacing['configuration'];
    duration_ms: number;
    news_count: number;
    delivered_count: number;
    due_at: string | null;
  } | null;
};

export default function PacingEditor({
  sessionId,
  readOnly = false,
  value,
  daily = true,
}: {
  sessionId: number;
  readOnly?: boolean;
  value?: Pacing;
  daily?: boolean;
}) {
  const [saved, setSaved] = useState<Pacing | null>(value || null);
  const [draft, setDraft] = useState<Pacing['configuration'] | null>(
    value?.configuration || null,
  );
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accept = (next: Pacing) => {
    setSaved(next);
    setDraft(next.configuration);
    setDirty(false);
  };
  useEffect(() => {
    if (value) {
      // Keep the edit's base revision: another trainer cannot silently overwrite it.
      setSaved(previous =>
        dirty && previous
          ? { ...previous, current_day: value.current_day }
          : value,
      );
      if (!dirty) setDraft(value.configuration);
    }
  }, [value]); // Preserve a trainer's in-progress edits across market ticks.
  useEffect(() => {
    if (value) return;
    const controller = new AbortController();
    void nativeRequest<Pacing>(
      `/fast/sessions/${sessionId}/pacing/`,
      undefined,
      controller.signal,
    )
      .then(next => {
        if (next && !controller.signal.aborted) accept(next);
      })
      .catch(reason => {
        if (!controller.signal.aborted) setError(String(reason));
      });
    return () => controller.abort();
  }, [sessionId]);
  const change = (
    key: keyof Pacing['configuration'],
    next: string | number | null,
  ) => {
    if (draft && next !== null) {
      setDraft({ ...draft, [key]: next });
      setDirty(true);
    }
  };
  const save = async () => {
    if (!draft || !saved || busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = await nativeRequest<Pacing>(
        `/fast/sessions/${sessionId}/pacing/`,
        {
          configuration: draft,
          expected_revision: saved.revision,
        },
      );
      if (next) accept(next);
    } catch (reason) {
      setError(String(reason));
    } finally {
      setBusy(false);
    }
  };
  if (!draft) return error ? <Alert type="error" title={error} /> : null;
  const number = (
    key: keyof Pacing['configuration'],
    label: string,
    min = 1,
  ) => (
    <label>
      {label}
      <br />
      <InputNumber
        aria-label={label}
        value={draft[key] as number}
        min={min}
        max={86400000}
        precision={0}
        disabled={readOnly || busy || !daily}
        onChange={v => change(key, v)}
      />
    </label>
  );
  return (
    <Space orientation="vertical" style={{ width: '100%' }}>
      <Typography.Paragraph>
        Daily sessions advance automatically. A day without news lasts X
        milliseconds; a day with N stories lasts Y × N milliseconds. Changes
        take effect on the next day.
      </Typography.Paragraph>
      {!daily && (
        <Alert
          type="info"
          title="News-to-news sessions continue to use manual advancement."
        />
      )}
      <Space wrap align="start">
        {number('empty_day_ms', 'X: day without news (ms)')}
        {number('news_item_ms', 'Y: time per story (ms)')}
        <label>
          News delivery
          <br />
          <Select
            aria-label="News delivery"
            value={draft.delivery}
            style={{ width: 160 }}
            disabled={readOnly || busy || !daily}
            onChange={v => change('delivery', v)}
            options={[
              { value: 'bulk', label: 'All together' },
              { value: 'staggered', label: 'Staggered' },
            ]}
          />
        </label>
        {draft.delivery === 'staggered' && (
          <>
            {number('news_interval_ms', 'Time between batches (ms)')}
            {number('news_quiet_ms', 'News-free time at end (ms)', 0)}
          </>
        )}
      </Space>
      {saved?.current_day && (
        <Typography.Text type="secondary">
          {saved.current_day.date}: {saved.current_day.duration_ms} ms ·{' '}
          {saved.current_day.delivered_count}/{saved.current_day.news_count}{' '}
          stories released
        </Typography.Text>
      )}
      {!readOnly && (
        <Button
          type="primary"
          disabled={!dirty || !daily}
          loading={busy}
          onClick={save}
        >
          Save pacing
        </Button>
      )}
      {error && (
        <Alert
          type="error"
          title={error}
          action={
            value && (
              <Button
                onClick={() => {
                  accept(value);
                  setError(null);
                }}
              >
                Reload pacing
              </Button>
            )
          }
        />
      )}
    </Space>
  );
}
